import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { useOrganizations } from '@/state/useOrganizations';
import { useAuth } from 'hooks/useAuth';
import { can } from 'definitions/capabilities';
import { Panel } from '../../ConsolePanel';

/**
 * The account's side of the two-level learnings contribution switch. It governs every
 * website the account owns; a single website can still be taken out from its own
 * settings. Nothing reads the value yet: the global intelligence engine is a later
 * feature, and the terms make contribution opt-out, so the way to opt out has to exist
 * before the first learning card does.
 *
 * Its own panel with an immediate save rather than a field in the Organization form: a
 * consent choice should not sit behind "Save changes" next to the company name.
 */
export const LearningsContribution = () => {
  const { user, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const { updateOrganization } = useOrganizations();
  const company = user!.company;
  const canManage = can(user, 'learnings:manage');

  const [contributes, setContributes] = useState(company.learningsContribution !== 'opted-out');
  const [isSaving, setIsSaving] = useState(false);

  const onToggle = async (checked: boolean) => {
    setContributes(checked);
    setIsSaving(true);
    try {
      await updateOrganization(company.id, { learningsContribution: checked ? 'contribute' : 'opted-out' });
      await refreshUser();
      // A client account's own row is also what the website list reads, through this
      // query. Without it the website settings keep showing the old state until reload.
      await queryClient.invalidateQueries({ queryKey: ['CLIENT_ACCOUNTS'] });
      toast.success(
        checked ? 'This account now contributes learnings.' : 'This account no longer contributes learnings.',
      );
    } catch (error) {
      setContributes(!checked);
      toast.error(error instanceof Error ? error.message : 'Could not save the setting.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Panel
      title="Learnings"
      description="Whether this account's experiment learnings feed Pertento's global intelligence, which learns what kinds of change work across websites."
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <label htmlFor="learnings-contribution" className="micro-label block cursor-pointer">
            Contribute learnings
          </label>
          <p className="supporting m-0 mt-1">
            Shared: change types, page types, the learning sentence, the outcome and the lift of each concluded test.
            Never your pages, code, screenshots, hypotheses, experiment names or URLs. Opting out does not change your
            own learnings, and any website can be taken out on its own from its settings.
            {!canManage && ' Only an account Owner or Admin can change this.'}
          </p>
        </div>
        <Switch
          id="learnings-contribution"
          checked={contributes}
          onCheckedChange={onToggle}
          disabled={!canManage || isSaving}
          className="mt-0.5 shrink-0"
        />
      </div>
    </Panel>
  );
};
